import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { CLIENT_ID } from '@environments/environment';
import { Subject } from 'rxjs';
import { ONLY_IN_STOCK_FIELD, ONLY_IN_STORE_FIELD } from '../models/constants/only-in-stock.constant';
import { TypeFilter } from '../models/enums/TypeFilter.enum';
import { FieldConfig, Filter, Filters, FiltersDTO } from '../models/types/Filters.type';

@Injectable()
export class FiltersService {

  static readonly ONLY_IN_STOCK_OPTION = `app.ois.filter.${CLIENT_ID}`;

  static readonly STORE_PEZZOTTI_COME_PORTO_RICO = ["457"];

  private _form!: FormGroup;
  public get form(): FormGroup {
    return this._form;
  }

  //Dati ricevuti dal BE
  private _filters: WritableSignal<FiltersDTO> = signal([]);
  public filters: Signal<FiltersDTO> = computed(() => this._filters());
  public areFiltersVisible: Signal<boolean> = computed(() => this._filters().length > 0);

  //Array di campi, costruito sulla falsa riga di _filters, da ciclare per renderizzare tutti i campi del form
  private _fields: WritableSignal<FiltersDTO> = signal([]);
  public fields: Signal<FiltersDTO> = computed(() => this._fields());

  private _submitSubject = new Subject<Filters>();
  public submit$ = this._submitSubject.asObservable();

  private _resetSubject = new Subject<any>();
  public reset$ = this._resetSubject.asObservable();

  private _onlyInStockFilter: Filter | null = null;
  public get onlyInStockFilter(): Filter | null {
    return this._onlyInStockFilter
  }

  private _onlyInStockptions: WritableSignal<Filters> = signal([]);
  public onlyInStockptions: Signal<Filters> = computed(() => this._onlyInStockptions());

  constructor(private readonly fb: FormBuilder) {
    this._form = this.fb.group({});
  }

  setOnlyInStockFilter(value: Filter | null, avoidSavingInLocalStorage?: boolean) {
    this._onlyInStockFilter = value;
    if (avoidSavingInLocalStorage) return;
    this.setOnlyInStockOptionInLocalStorage(value);
  }

  setOnlyInStockOptionInLocalStorage(value: Filter | null) {
    localStorage.setItem(FiltersService.ONLY_IN_STOCK_OPTION, JSON.stringify(value));
  }

  getOnlyInStockOptionFromLocalStorage(): Filter | null {
    let opt = localStorage.getItem(FiltersService.ONLY_IN_STOCK_OPTION);
    return opt && JSON.parse(opt);
  }

  static removeOnlyInStockOptionInLocalStorage() {
    localStorage.removeItem(FiltersService.ONLY_IN_STOCK_OPTION);
  }

  setOnlyInStockOptions(options: Filters) {
    this._onlyInStockptions.set(options);
  }

  /**
   * Setta sia i filtri che i campi
   * @param filters
   */
  public setFilters(filters: FiltersDTO) {
    this._filters.set(filters);
    let fields: FiltersDTO = this.buildFormFields(filters);
    this._fields.set(fields);
  }

  /**
   * Costruisco il form e i campi che lo compongono
   * @param filters
   * @returns
   */
  private buildFormFields(filters: FiltersDTO, resettingValues?: boolean): FiltersDTO {
    //Ogni volta che costruisco il form ricostruisco da zero per evitare il mantenimento dei valori
    // this._form = this.fb.group({});
    let fields: FiltersDTO = [];
    filters.forEach(filter => {
      let { field, value, type } = filter;
      let validators: Validators = [];
      let config: FieldConfig = {
        field,
        type,
        label: `filter.${field}.label`,
        placeholder: `filter.${field}.placeholder`,
      }
      //Flag per pushare il field nell'array
      let isFieldValid: boolean = true;
      switch (type) {
        case TypeFilter.TEXT: {
          let { texts } = filter;
          if (texts) {
            // validators = [this.inSetValidator(texts)];
            config.options = texts.map(t => {
              return {
                idTerm: t,
                label: t
              }
            });
          }
          value = [];
          //Invalido il field se è una multi-select senza options
          if (!config.options?.length) {
            isFieldValid = false;
          }
          break;
        }
        case TypeFilter.CURRENCY: {
          // if (filter.priceRange) {
          //   let { min, max } = filter.priceRange;
          //   validators = [Validators.min(min), Validators.max(max)];
          // }
          value = [];
          break;
        }
        case TypeFilter.TAXONOMY: {
          config.options = filter.terms;
          //Invalido il field se è una multi-select senza options
          if (!config.options?.length) {
            isFieldValid = false;
          }
          value = [];
          break;
        }
        case TypeFilter.DATE:
        case TypeFilter.COLLECTION_OF_TAXONOMY:
        case TypeFilter.NEWIN:
        case TypeFilter.SPECIAL_EDITION:
        case TypeFilter.CLASSIFICATIONS:
        case TypeFilter.BOOLEAN:
        case TypeFilter.STOCK:
        case TypeFilter.STORE_STOCK:
        case TypeFilter.NONE:
        default: {
          console.warn("Filtro non censito", type)
          break;
        }
      }
      let control = new FormControl(value, validators);
      this._form.addControl(field, control);

      if (isFieldValid) {
        fields.push({ ...filter, config });
      }
    });

    return fields;
  }

  /**
   * Ritorna il formControl in base al name passato come parametro
   * @param formControlName
   * @returns
   */
  public getFormControl(formControlName: string): FormControl {
    return this._form.get(formControlName) as FormControl;
  }

  /**
   * Validatore che controlla che il valore inserito sia incluso in quelli consentiti
   * @param validSet
   * @returns
   */
  private inSetValidator(validSet: any[]): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        return validSet.includes(control.value) ? null : { notInSet: true };
      } else {
        return null;
      }
    };
  }

  /**
   * Formatta i valodi del form in Filters, ovvero in un array di oggetti contentente "field" e "values"
   * @returns
   */
  private parseFormValueToFilters(): Filters {
    let filters: Filters = [];
    if (this.onlyInStockFilter) {
      filters = [this.onlyInStockFilter, ...filters];
    }
    Object.keys(this.form.value).forEach((key: string) => {
      let field = key;
      let values: any[] = [];
      let val: any = this.form.value[key];
      switch (true) {
        case Array.isArray(val): {
          values = val.map((v: any) => v ? v.toString() : "");
          break;
        }
        case !!val: {
          values = [val]
          break;
        }
        default: break;
      }
      // if (values.filter(v => v !== null).length > 0) {
      //   filters.push({ field, values });
      // }
      if (values.length > 0) {
        filters.push({ field, values });
      }
    })
    return filters;
  }

  /**
   * Submit del form
   */
  public submit() {
    this._submitSubject.next(this.parseFormValueToFilters());
  }

  /**
   * Reset del form
   * @param avoidSubmit Evita di invocare la submit, resetta solo i valori del form
   * @returns
   */
  public reset(avoidSubmit?: boolean) {
    this._form.reset(this.resettedFormValue());
    this._resetSubject.next(true);
    if (!avoidSubmit) {
      this.submit();
    }
  }

  private resettedFormValue(): any {
    let formValue = { ...this._form.value };
    Object.keys(formValue).forEach(key => {
      let val = formValue[key];
      if (Array.isArray(val)) {
        formValue[key] = [];
      } else {
        formValue[key] = null;
      }
    });
    return formValue;
  }

  getSelectedOptionIndex(): number {
    let filter = this.onlyInStockFilter;
    if (filter?.field === ONLY_IN_STORE_FIELD) {
      //Sto filtrando per My Stock Store
      return 2;
    }
    if (filter?.field === ONLY_IN_STOCK_FIELD) {
      //Verifico se il primo valore di "values" è true o false
      if (filter.values[0] === "true") {
        return 1;
      }
    }
    return 0;
  }

}
